/**
 * ROUTE-GATED IDENTITY — queue items 7+13, Haley's rulings 7 and 13.
 *
 * Ruling 7: "it depends on the route sometimes they know sometimes they don't
 * so do what you need to do to make that happen."
 * Ruling 13: "Kya does not know that" — a past-life identity is a route-gated
 * secret. The current incarnation does NOT know by default; others may know
 * and trade it as rumour. The archive shows the reveal happening ON THE PAGE:
 * "You were a goddess. … you followed our sister Lachesis into this life."
 * (haley-finds-out-shes-cool-2, 2022-12-01) and circulating as rumour: "At
 * first it was just a rumor, a fate was reincarnated." (wtf-happened,
 * 2023-08-08).
 *
 * WHY THE LINKS LIVE HERE AND NOWHERE ELSE. `buildWorldCast` broadcasts
 * `relationships`/`backstory` to the whole cast, and `character_secrets`
 * renders into the character's OWN prompt ("YOUR SECRETS"). A past-life link
 * in ANY of those fields leaks — either to everyone, or to the very character
 * who must not know. So the link's only durable home is this registry, and
 * its only path into a prompt is the per-scene latch: once a route says it on
 * the page, `identity_revealed:<key>` latches (one-way, like
 * `fate_recognized`) and the gate's liveLine is injected FOR THAT ROUTE ONLY.
 *
 * This is the same window-DETECTS / flag-REMEMBERS pattern as the Fates latch
 * in ChannelView (audit P1-9): the scan window only detects; the scene flag
 * remembers. Disguise gates (Alyce/Moros) differ from past-life gates in one
 * way only: the disguised character's own fields DO carry the truth (Moros
 * knows he is Moros), while a past-life link appears in no field on either
 * side (Melody's prompt and Kya's prompt are both free of it — verified by
 * test/fatesKnowledge.test.js).
 *
 * Archive proof for each gate is cited inline — rule 6: proof for everything.
 */

export const IDENTITY_GATES = [
  {
    key: 'alyce_moros',
    kind: 'disguise',
    chars: ['alyce', 'moros'],
    // Reveal-shaped only: a bare co-mention of the two names must not open
    // the gate — the linking verb / disguise language is required.
    patterns: [
      /\b(?:alyce|max)\b[^.!?\n]{0,120}\b(?:is|was|being|really|actually|secretly)\b[^.!?\n]{0,60}\bmoros\b/i,
      /\bmoros\b[^.!?\n]{0,60}\b(?:in\s+)?disguise[sd]?\b/i,
      /\bmoros\b[^.!?\n]{0,120}\b(?:posing|pretending|hiding|disguised)\b[^.!?\n]{0,60}\b(?:as\s+)?(?:alyce|max|a\s+(?:child|kid|girl))\b/i,
      /\byou'?re\s+moros\b/i,
      /\bdrops?\s+the\s+alyce\s+disguise\b/i,
    ],
    liveLine:
      "THIS ROUTE HAS ESTABLISHED that 'Alyce' (who has also gone by 'Max') is the god MOROS in disguise. " +
      'That knowledge is LIVE in this scene: characters present may know it and react to it. ' +
      'This applies to THIS route only — in any other scene the disguise holds and nobody suspects.',
  },
  {
    key: 'kya_melody',
    kind: 'past_life',
    chars: ['kya_sg1', 'kya_sg3', 'melody'],
    // Proof: kya-sold-off 2021-07-16 — "tapping into the Melody part of her
    // without realizing it"; 2021-07-18 (Leiden) — "Melody was a woman who…I
    // used to know" — others know while Kya does not.
    patterns: [
      /\bmelody\b[^.!?\n]{0,140}\b(?:past life|previous life|previous incarnation|reincarnat\w*)\b/i,
      /\b(?:kya|you)\b[^.!?\n]{0,80}\b(?:were|was|used to be)\b[^.!?\n]{0,40}\bmelody\b/i,
      /\bmelody\b[^.!?\n]{0,80}\b(?:became|come back as|returned as)\b[^.!?\n]{0,40}\bkya\b/i,
      /\bthe\s+melody\s+part\s+of\s+(?:her|you)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that MELODY was a past life of KYA. ' +
      'Until it was said here, Kya did NOT know it — a past-life identity is never self-known by default; ' +
      'others may already have known and traded it as rumour. It is live for THIS route only.',
  },
  {
    key: 'kya_lily',
    kind: 'past_life',
    chars: ['kya_sg1', 'kya_sg3'],
    // Proof: kya-college-2 2021-01-27 — "Lily, Kya... I suppose it's all one
    // in the same now, isn't it?" (after the memories return on the page).
    patterns: [
      /\b(?:kya|you)\b[^.!?\n]{0,80}\b(?:were|was|used to be)\b[^.!?\n]{0,40}\blily\b/i,
      // "Lily, Kya... I suppose it's all one in the same now, isn't it?" —
      // the ellipsis means periods must be allowed inside the span here.
      /\blily\b[^!?\n]{0,60}\bkya\b[^!?\n]{0,90}\b(?:one in the same|one and the same|same (?:person|soul|woman))\b/i,
      /\bkya\b[^!?\n]{0,60}\blily\b[^!?\n]{0,90}\b(?:one in the same|one and the same|same (?:person|soul|woman))\b/i,
      /\blily\b[^.!?\n]{0,140}\b(?:past life|previous life|previous incarnation|reincarnat\w*)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that LILY was a past life of KYA ' +
      '(she ruled the winter kingdom beside Zander in that life). ' +
      'Until it was said here, Kya did NOT know it. It is live for THIS route only.',
  },
  {
    key: 'zeb_zander',
    kind: 'past_life',
    chars: ['zander'],
    // Proof: kya-college-2 2021-01-27 — "When she called him Zander, his
    // sleepy brain didn't process that he wasn't the pixie prince anymore and
    // he was a siren prince." Zeb (siren prince) reincarnates Zander (pixie
    // prince). There is no `zeb` record yet; the gate still latches so the
    // knowledge is scene-scoped when the players play it.
    patterns: [
      /\bzeb\b[^.!?\n]{0,80}\b(?:were|was|used to be)\b[^.!?\n]{0,40}\bzander\b/i,
      /\bzander\b[^.!?\n]{0,100}\b(?:became|come back as|returned as|reincarnat\w*)\b[^.!?\n]{0,40}\bzeb\b/i,
      /\bwasn'?t\s+the\s+pixie\s+prince\s+anymore\b/i,
      /\bzander\b[^.!?\n]{0,140}\b(?:past life|previous life|previous incarnation)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that ZANDER (the pixie prince) was a past life of ZEB ' +
      '(the siren prince). Until it was said here, the current incarnation did NOT remember it. ' +
      'It is live for THIS route only.',
  },
  {
    key: 'elizabeth_atropos',
    kind: 'past_life',
    chars: ['atropos'],
    // Proof of the on-page reveal shape: haley-finds-out-shes-cool-2
    // 2022-12-01 (Moros): "You were a goddess. … you followed our sister
    // Lachesis into this life." The Elizabeth here is Atropos's mortal life
    // in the vampire timelines — ALISTAIR'S LATE WIFE, mother of Charlie and
    // Caroline ("I am Elizabeth and I am Haley. I am...someone else
    // entirely", haley-nolan 2020-09-13). NOT `elizabeth_the_third`, which
    // is Jumin's cat — a name collision already documented in
    // scripts/canon_exclusions.py. The wife has no record of her own.
    patterns: [
      /\belizabeth\b[^.!?\n]{0,100}\b(?:is|was|really|actually)\b[^.!?\n]{0,60}\batropos\b/i,
      /\batropos\b[^.!?\n]{0,100}\b(?:lived|living|life)\b[^.!?\n]{0,60}\belizabeth\b/i,
      /\byour name was atropos\b/i,
      /\belizabeth\b[^.!?\n]{0,140}\b(?:past life|previous life|mortal incarnation|reincarnat\w*)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that ELIZABETH is a mortal incarnation of ATROPOS. ' +
      'Until it was said here, Elizabeth did NOT know she was Atropos — she learns it only by being told. ' +
      'Others may know and trade it as rumour. It is live for THIS route only.',
  },
  {
    key: 'trix_azrael',
    kind: 'disguise',
    chars: ['trix'],
    // Proof: a-tricky-micah-cruz-route (last three days of the route) — "They
    // didn't know about Trix's true identity"; she is named as AZRAEL, an
    // angel planted alongside Micah. The alternate_routes record carries it;
    // that column is deliberately unfetched, so this gate is the only path.
    patterns: [
      /\btrix\b[^.!?\n]{0,120}\b(?:is|was|being|really|actually|secretly)\b[^.!?\n]{0,60}\bazrael\b/i,
      /\bazrael\b[^.!?\n]{0,100}\b(?:posing|planted|disguised|hiding|undercover)\b[^.!?\n]{0,60}\b(?:as\s+)?trix\b/i,
      /\btrix\b[^.!?\n]{0,80}\bisn'?t\s+a\s+real\s+wolf\b/i,
      /\byou'?re\s+azrael\b/i,
    ],
    liveLine:
      "THIS ROUTE HAS ESTABLISHED that TRIX is secretly the angel AZRAEL, planted in the pack. " +
      'That knowledge is LIVE in this scene: characters present may know it and react to it. ' +
      'This applies to THIS route only — in any other scene Trix is an ordinary pack she-wolf and nobody suspects.',
  },
  {
    key: 'evelyn_eva_fallon',
    kind: 'past_life',
    chars: ['evelyn'],
    // Proof: the-kingdom-fallon — the Fianna retrieve Evelyn as the
    // reincarnation of King Lir's dead queen Eva; Cailte: "You really are
    // her. You just don't know it yet." Diarmund: "I'm your brother, you just
    // have to get over it, Eva." She spends the route insisting she is not Eva.
    patterns: [
      /\b(?:evelyn|you)\b[^.!?\n]{0,100}\b(?:reincarnation|rebirth|reborn)\b[^.!?\n]{0,60}\beva\b/i,
      /\byou\s+really\s+are\s+her\b[\s\S]{0,120}\beva\b/i, // the canon reveal crosses a sentence boundary
      /\beva\b[^.!?\n]{0,100}\b(?:came back|come back|returned|reborn|reincarnat\w*)\b[^.!?\n]{0,60}\b(?:as\s+)?evelyn\b/i,
      /\byou(?:'re| are)\s+eva\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that EVELYN is the reincarnation of EVA, King Lir\u2019s dead queen. ' +
      'Until it was said here, Evelyn did NOT know it and does not accept it easily; the Fianna and King Lir may already know. ' +
      'It is live for THIS route only.',
  },
  {
    key: 'kaleb_april',
    kind: 'past_life',
    chars: ['kaleb'],
    // Proof: actual-kaleb-romance (alternate_routes record) — a reincarnation
    // route with April "where they died together in a previous life."
    patterns: [
      /\b(?:kaleb|april)\b[^.!?\n]{0,120}\bdied\s+together\b[^.!?\n]{0,80}\b(?:previous|past)\s+life\b/i,
      /\b(?:kaleb|you)\b[^.!?\n]{0,80}\b(?:knew|loved|lost)\b[^.!?\n]{0,40}\bapril\b[^.!?\n]{0,80}\b(?:previous|past|another)\s+life\b/i,
      /\bapril\b[^.!?\n]{0,140}\b(?:past life|previous life|reincarnat\w*)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that KALEB and APRIL shared a previous life and died together in it. ' +
      'Until it was said here, the current incarnations did NOT remember it. It is live for THIS route only.',
  },
  {
    key: 'evelyn_dimitri_secondlife',
    kind: 'past_life',
    chars: ['dimitri'],
    // Proof: dimitri-route-end + evelyn-college-2 (alternate_routes record) —
    // second-life lines in which Evelyn has already lived and lost a life
    // with him AND HE HAS NOT: she may remember; Dimitri must not know until
    // the route says it.
    patterns: [
      /\b(?:evelyn|she|i)\b[^.!?\n]{0,100}\b(?:already\s+)?lived\b[^.!?\n]{0,60}\b(?:a\s+)?life\b[^.!?\n]{0,60}\b(?:with\s+)?(?:you|dimitri)\b/i,
      /\b(?:we|you and i)\b[^.!?\n]{0,80}\b(?:were together|married|loved)\b[^.!?\n]{0,80}\b(?:previous|past|another|first)\s+life\b/i,
      /\bdimitri\b[^.!?\n]{0,140}\b(?:past life|previous life|second life|first life)\b/i,
    ],
    liveLine:
      'THIS ROUTE HAS ESTABLISHED on the page that EVELYN has already lived and lost a whole life with DIMITRI \u2014 and he had not known it. ' +
      'She may carry those memories; until it was said here, Dimitri did NOT know. It is live for THIS route only.',
  },
];

/** Scene-flag key for a gate. One-way latch, same family as fate_recognized. */
export function gateFlagKey(gateKey) {
  return `identity_revealed:${gateKey}`;
}

/**
 * Scan a window of scene text and return the keys of every gate whose reveal
 * is on the page. The caller latches them; this function only detects.
 */
export function detectIdentityReveals(text) {
  const hay = String(text || '');
  if (!hay) return [];
  const hit = [];
  for (const gate of IDENTITY_GATES) {
    if (gate.patterns.some((rx) => rx.test(hay))) hit.push(gate.key);
  }
  return hit;
}

/**
 * The prompt block for every gate latched in this scene, or '' when none are.
 * Byte-stable for a given set of latched keys — safe for the cached half.
 */
export function buildGateBlocks(latchedKeys) {
  const keys = Array.isArray(latchedKeys) ? latchedKeys : [];
  const lines = IDENTITY_GATES.filter((g) => keys.includes(g.key)).map((g) => g.liveLine);
  return lines.join('\n');
}
